import {
  DragEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GridLayout, { getCompactor, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import CommandDashboardTile from './command-dashboard-tile';
import { useCommandDashboardTilesStore } from './command-dashboard-tiles-store';
import {
  buildTileLayout,
  columnAt,
  columnWidthsForRow,
  GRID_COLS,
  insertTile,
  isSameTarget,
  moveTileTo,
  resolveTarget,
  ROW_GAP,
  ROW_HEIGHT,
  RowBand,
  rowGeometry,
  TILE_GAP,
  TileDefinitionRows,
  TileTarget,
} from './grid-layout-math';
import {
  COMMAND_DASHBOARD_TILE_SIZE_PROFILES,
  COMMAND_DASHBOARD_TILE_LIBRARY,
  CommandDashboardTileDefinition,
  CommandDashboardTileKey,
  FULL_WIDTH_TILE_COLUMNS,
} from './tile-config';
import {
  CommandDashboardTileBodyComponents,
  TILE_CONTENT_COMPONENTS,
} from './tile-content-registry';
import TileDropPreview from './tile-drop-preview';
import Box from '@mui/material/Box';

export type { CommandDashboardTileBodyProps } from './tile-content-registry';

// Used before the container has been measured, and under jsdom in tests (whose ResizeObserver
// never fires within a synchronous test, so this is what tests actually render against
// regardless of any mocked container width). A multiple of GRID_COLS (12) and TILE_GAP (24) so
// every column position lands on a whole pixel - react-grid-layout otherwise distributes a
// fractional remainder unevenly across items in the same row.
const FALLBACK_WIDTH = 1284;

/** Keeps an empty dashboard big enough to be a drop target. */
const MIN_HEIGHT = 320;

/** Opacity of a tile shown at the position it is being dragged towards. */
const MOVING_TILE_OPACITY = 0.4;

const GRID_CONFIG = {
  cols: GRID_COLS,
  rowHeight: ROW_HEIGHT,
  // The horizontal margin is the gutter between side-by-side tiles. The
  // vertical one stays zero so a one-pixel row makes an item's `h` its exact
  // pixel height, letting a tile be sized to its measured content.
  margin: [TILE_GAP, 0] as [number, number],
  containerPadding: [0, 0] as [number, number],
};

const DRAG_CONFIG = { enabled: false };
const RESIZE_CONFIG = { enabled: false };
const PASSTHROUGH_COMPACTOR = getCompactor(null, true);

const rowUnitsFor = (tile: CommandDashboardTileDefinition): number =>
  COMMAND_DASHBOARD_TILE_SIZE_PROFILES[tile.sizeProfile].rowUnits;

interface CommandDashboardGridProps {
  tileRows: TileDefinitionRows;
  onRemove: (key: CommandDashboardTileKey) => void;
  onDropTile: (key: CommandDashboardTileKey, target: TileTarget) => void;
  onMoveTile: (key: CommandDashboardTileKey, target: TileTarget) => void;
  /**
   * Shown over the grid while it holds nothing. The grid stays mounted behind
   * it so an empty dashboard is still somewhere tiles can be dropped.
   */
  emptyState?: ReactNode;
  /**
   * Override the default tile-body registry (tests that only care about layout
   * pass `{}` so every tile is the header-only shell). Unlisted keys render the
   * shell with no body.
   */
  tileContentComponents?: CommandDashboardTileBodyComponents;
}

export default function CommandDashboardGrid({
  tileRows,
  onRemove,
  onDropTile,
  onMoveTile,
  emptyState,
  tileContentComponents = TILE_CONTENT_COMPONENTS,
}: CommandDashboardGridProps) {
  const { width, containerRef } = useContainerWidth({
    initialWidth: FALLBACK_WIDTH,
  });
  const gridRef = useRef<HTMLDivElement | null>(null);
  const draggingTileKey = useCommandDashboardTilesStore(
    (state) => state.draggingTileKey
  );
  const setDraggingTileKey = useCommandDashboardTilesStore(
    (state) => state.setDraggingTileKey
  );
  const [dropTarget, setDropTarget] = useState<TileTarget | null>(null);
  const gridWidth = width || FALLBACK_WIDTH;

  const draggingTile = useMemo(
    () =>
      COMMAND_DASHBOARD_TILE_LIBRARY.find(
        (tile) => tile.key === draggingTileKey
      ),
    [draggingTileKey]
  );
  // A tile already on the dashboard is being moved; anything else is being
  // added from the library.
  const isMove = useMemo(
    () => tileRows.some((row) => row.some((tile) => tile === draggingTile)),
    [draggingTile, tileRows]
  );
  const bands = useMemo(() => rowGeometry(tileRows), [tileRows]);

  /**
   * Row bands as they were when the drag entered the grid.
   *
   * The preview reflows the tiles it passes over, which changes their heights
   * and so would change the bands underneath the cursor. Hit testing against a
   * moving target makes the drop zone flip back and forth without the cursor
   * moving, so the drag is resolved against the layout the user saw when they
   * started.
   */
  const dragBandsRef = useRef<RowBand[] | null>(null);

  const endDrag = useCallback(() => {
    dragBandsRef.current = null;
    setDropTarget(null);
  }, []);

  // The drag can end anywhere, including outside the window, and the drag
  // source clears the dragged key on `dragend` wherever that happens.
  useEffect(() => {
    if (!draggingTileKey) {
      endDrag();
    }
  }, [draggingTileKey, endDrag]);

  /**
   * The arrangement the dashboard would have if the drag were dropped now. The
   * grid renders this rather than the committed rows, so the tiles either side
   * of the drop resize and shift into place before the user commits.
   */
  const previewRows = useMemo(() => {
    if (!draggingTile || !dropTarget) {
      return tileRows;
    }
    return isMove
      ? moveTileTo(tileRows, draggingTile, dropTarget, rowUnitsFor)
      : insertTile(tileRows, draggingTile, dropTarget, rowUnitsFor);
  }, [draggingTile, dropTarget, isMove, tileRows]);

  const layout = useMemo(() => buildTileLayout(previewRows), [previewRows]);

  /**
   * Columns each tile spans, which is what tells a tile (and its body) which of
   * its sizes it is being drawn at. A dashboard narrower than Figma's canvas
   * renders every size proportionally smaller, so the span rather than the
   * rendered pixel width is what identifies the size.
   */
  const columnsByKey = useMemo(() => {
    const columns = new Map<CommandDashboardTileKey, number>();
    previewRows.forEach((row) => {
      const widths = columnWidthsForRow(row);
      row.forEach((tile, index) => columns.set(tile.key, widths[index]));
    });
    return columns;
  }, [previewRows]);

  /**
   * Rows as they will be once the drag is out of the way.
   *
   * A move drops the tile back into the rows it was taken out of, so a row it
   * is leaving has a slot going spare. Counting the tile against the row it is
   * being dragged out of would read a full row as having no room and bounce the
   * tile into a row of its own, leaving no way to reorder a full row at all.
   *
   * Empty rows are kept rather than dropped so the row indices still line up
   * with the bands being hit tested against.
   */
  const hitTestRows = useMemo(
    () =>
      isMove && draggingTile
        ? tileRows.map((row) => row.filter((tile) => tile !== draggingTile))
        : tileRows,
    [draggingTile, isMove, tileRows]
  );

  const targetFromEvent = useCallback(
    (e: DragEvent<HTMLDivElement>): TileTarget | null => {
      const rect = gridRef.current?.getBoundingClientRect();
      if (!rect) {
        return null;
      }
      if (!dragBandsRef.current) {
        dragBandsRef.current = bands;
      }
      return resolveTarget(
        hitTestRows,
        dragBandsRef.current,
        columnAt(e.clientX - rect.left, gridWidth),
        e.clientY - rect.top,
        draggingTile,
        rowUnitsFor
      );
    },
    [bands, draggingTile, gridWidth, hitTestRows]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      // Only tiles are droppable, so a file or a text selection dragged over
      // the dashboard is left alone.
      if (!draggingTileKey) {
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = isMove ? 'move' : 'copy';

      const target = targetFromEvent(e);
      if (target) {
        setDropTarget((current) =>
          isSameTarget(current, target) ? current : target
        );
      }
    },
    [draggingTileKey, isMove, targetFromEvent]
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      // Moving between tiles fires a leave for the one being left, so the
      // preview should only drop when the cursor exits the grid altogether.
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
        return;
      }
      endDrag();
    },
    [endDrag]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!draggingTileKey) {
        return;
      }
      e.preventDefault();
      const target = dropTarget ?? targetFromEvent(e);
      const commit = isMove ? onMoveTile : onDropTile;
      endDrag();
      setDraggingTileKey(null);
      if (target) {
        commit(draggingTileKey, target);
      }
    },
    [
      draggingTileKey,
      dropTarget,
      endDrag,
      isMove,
      onDropTile,
      onMoveTile,
      setDraggingTileKey,
      targetFromEvent,
    ]
  );

  /**
   * A tile being moved keeps rendering rather than being swapped for a
   * placeholder: replacing it would unmount the element the drag started from,
   * and a drag whose source has gone never reports that it ended.
   */
  const renderTile = (tile: CommandDashboardTileDefinition) => {
    if (tile.key === draggingTileKey && !isMove) {
      return <TileDropPreview tile={tile} />;
    }
    const TileBody = tileContentComponents?.[tile.key];
    const columns = columnsByKey.get(tile.key) ?? FULL_WIDTH_TILE_COLUMNS;
    return (
      <CommandDashboardTile
        tile={tile}
        onRemove={onRemove}
        columns={columns}
        onDragStart={() => setDraggingTileKey(tile.key)}
        onDragEnd={() => setDraggingTileKey(null)}
      >
        {TileBody ? <TileBody tile={tile} columns={columns} /> : null}
      </CommandDashboardTile>
    );
  };

  return (
    <Box
      ref={containerRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{ flex: 1, position: 'relative' }}
    >
      <GridLayout
        innerRef={gridRef}
        width={gridWidth}
        layout={layout}
        gridConfig={GRID_CONFIG}
        dragConfig={DRAG_CONFIG}
        resizeConfig={RESIZE_CONFIG}
        compactor={PASSTHROUGH_COMPACTOR}
        style={{ minHeight: MIN_HEIGHT }}
      >
        {previewRows.flat().map((tile) => (
          <div key={tile.key}>
            <Box
              sx={{
                height: '100%',
                pb: `${ROW_GAP}px`,
                boxSizing: 'border-box',
                containerType: 'inline-size',
                opacity:
                  tile.key === draggingTileKey && isMove
                    ? MOVING_TILE_OPACITY
                    : 1,
              }}
            >
              {renderTile(tile)}
            </Box>
          </div>
        ))}
      </GridLayout>
      {/*
        Rendered after the grid so it paints over it. Both are positioned with
        an automatic z-index, which leaves tree order to decide, and whatever
        the grid covers cannot be clicked however its pointer events are set.

        Keeping it inside the box that listens for drags matters too: events on
        anything interactive in here still bubble to those handlers, so the
        message never carves a hole out of the drop target.
      */}
      {emptyState && previewRows.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {emptyState}
        </Box>
      )}
    </Box>
  );
}
